// DeepSeek Chat API Service
import { chatConfig } from '../data/chatData';

// API Key from environment or config
const API_KEY = 'sk-7549b29d010a424a836d081a478552f4';

class DeepSeekChatService {
  constructor() {
    this.apiKey = API_KEY;
    this.baseUrl = chatConfig.apiEndpoint;
    this.model = chatConfig.model;
  }

  // Generate chat completion with context
  async generateChatCompletion(messages, context = {}) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'system',
              content: this.buildSystemPrompt(context)
            },
            ...messages
          ],
          max_tokens: chatConfig.maxTokens,
          temperature: chatConfig.temperature,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        success: true,
        content: data.choices[0].message.content,
        usage: data.usage,
        model: data.model
      };
    } catch (error) {
      console.error('DeepSeek API Error:', error);
      return {
        success: false,
        error: error.message,
        fallbackContent: this.getFallbackContent(context)
      };
    }
  }

  // Build system prompt based on context
  buildSystemPrompt(context) {
    let systemPrompt = chatConfig.systemPrompt;
    
    if (context.topic) {
      systemPrompt += `\n\nCurrent Topic: ${context.topic}`;
    }
    
    if (context.chatType) {
      switch (context.chatType) {
        case 'quick-quiz':
          systemPrompt += `\n\nMode: Quick Quiz Generation
Generate multiple-choice questions with explanations. Focus on HKDSE exam format and common student mistakes.`;
          break;
        case 'drill-exercise':
          systemPrompt += `\n\nMode: Drill Exercise Creation
Create step-by-step practice exercises with progressive difficulty. Include detailed explanations and exam tips.`;
          break;
        case 'direct-chat':
          systemPrompt += `\n\nMode: Direct Tutoring
Provide conversational help and answer student questions. Be encouraging and supportive.`;
          break;
      }
    }
    
    if (context.keywords && context.keywords.length > 0) {
      systemPrompt += `\n\nKey Concepts: ${context.keywords.join(', ')}`;
    }
    
    return systemPrompt;
  }

  // Generate quick quiz
  async generateQuickQuiz(topic, context = {}) {
    const prompt = chatConfig.quickQuizPrompt
      .replace('{topic}', topic)
      .replace('{count}', '5');

    const messages = [
      {
        role: 'user',
        content: prompt
      }
    ];

    return await this.generateChatCompletion(messages, {
      ...context,
      chatType: 'quick-quiz',
      topic
    });
  }

  // Generate drill exercise
  async generateDrillExercise(topic, context = {}) {
    const prompt = chatConfig.drillPrompt.replace('{topic}', topic);

    const messages = [
      {
        role: 'user',
        content: prompt
      }
    ];

    return await this.generateChatCompletion(messages, {
      ...context,
      chatType: 'drill-exercise',
      topic
    });
  }

  // Continue chat conversation
  async continueChat(messages, context = {}) {
    return await this.generateChatCompletion(messages, {
      ...context,
      chatType: 'direct-chat'
    });
  }

  // Get fallback content when API fails
  getFallbackContent(context) {
    const { chatType, topic } = context;
    
    switch (chatType) {
      case 'quick-quiz':
        return `# Quick Quiz: ${topic}

**Question 1:** Which of the following is an example of ${topic}?
A) Option A
B) Option B
C) Option C
D) Option D

**Correct Answer:** B
**Explanation:** This is a sample question. The actual quiz would be generated by AI.

*Note: AI service temporarily unavailable. Please try again later for personalized quiz questions.*`;

      case 'drill-exercise':
        return `# Drill Exercise: ${topic}

## Concept Review
${topic} is an important grammar concept for HKDSE English Language.

## Practice Questions
1. Complete the following sentence...
2. Identify the error in...
3. Choose the correct form...

## Tips for Success
- Practice regularly
- Review common mistakes
- Apply concepts in writing

*Note: AI service temporarily unavailable. Please try again later for personalized drill exercises.*`;

      case 'direct-chat':
        return `Hello! I'm here to help you with ${topic}. 

Unfortunately, I'm having trouble connecting to the AI service right now. Please try again in a few moments.

In the meantime, you can:
- Review the grammar concepts on this page
- Try the practice exercises
- Check out the exam tips section

I'll be back to help you soon! 😊`;

      default:
        return `I'm here to help you with ${topic}. Please try again in a moment as I'm having connection issues.`;
    }
  }

  // Parse quiz response into structured format
  parseQuizResponse(content) {
    const questions = [];
    const lines = content.split('\n');
    let currentQuestion = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('Question:')) {
        if (currentQuestion) {
          questions.push(currentQuestion);
        }
        currentQuestion = {
          question: trimmed.replace('Question:', '').trim(),
          options: [],
          correctAnswer: null,
          explanation: ''
        };
      } else if (trimmed.match(/^[A-D]\)/)) {
        if (currentQuestion) {
          currentQuestion.options.push(trimmed.substring(2).trim());
        }
      } else if (trimmed.startsWith('Correct Answer:')) {
        if (currentQuestion) {
          const answer = trimmed.replace('Correct Answer:', '').trim();
          currentQuestion.correctAnswer = answer.charCodeAt(0) - 65; // Convert A,B,C,D to 0,1,2,3
        }
      } else if (trimmed.startsWith('Explanation:')) {
        if (currentQuestion) {
          currentQuestion.explanation = trimmed.replace('Explanation:', '').trim();
        }
      }
    }
    
    if (currentQuestion) {
      questions.push(currentQuestion);
    }
    
    return questions;
  }

  // Parse drill response into structured format
  parseDrillResponse(content) {
    const sections = {
      conceptReview: '',
      exercises: [],
      tips: '',
      additionalPractice: ''
    };
    
    const lines = content.split('\n');
    let currentSection = 'conceptReview';
    let currentExercise = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed.includes('Practice Questions') || trimmed.includes('Exercises')) {
        currentSection = 'exercises';
      } else if (trimmed.includes('Tips') || trimmed.includes('Common Mistakes')) {
        currentSection = 'tips';
      } else if (trimmed.includes('Additional Practice')) {
        currentSection = 'additionalPractice';
      } else if (currentSection === 'exercises' && trimmed.match(/^\d+\./)) {
        if (currentExercise) {
          sections.exercises.push(currentExercise);
        }
        currentExercise = {
          instruction: trimmed,
          answer: '',
          explanation: ''
        };
      } else if (currentSection === 'conceptReview') {
        sections.conceptReview += line + '\n';
      } else if (currentSection === 'tips') {
        sections.tips += line + '\n';
      } else if (currentSection === 'additionalPractice') {
        sections.additionalPractice += line + '\n';
      }
    }
    
    if (currentExercise) {
      sections.exercises.push(currentExercise);
    }
    
    return sections;
  }

  // Test API connection
  async testConnection() {
    try {
      const response = await this.generateChatCompletion([
        {
          role: 'user',
          content: 'Hello, are you working?'
        }
      ]);
      
      return response.success;
    } catch (error) {
      console.error('API connection test failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export default new DeepSeekChatService(); 